
// src/pages/facturacion/index.jsx
  // Facturación — OC Nº idéntico al modal, con columna “Centro de Costo” por fila.
  // ✅ Soporta modo público SIN login:
  //    - CON Invoice ID + Token: usa RPC get_facturacion_invoice_public
  //    - SIN Invoice ID: Intenta RPC público get_facturacion_public o fallback
  // ✅ Mantiene UI/funciones del original (export PDF, links OC/Remito, tabla, observaciones)
  // ✅ Botón "Bandeja de facturación" blindado contra errores de Router.

  import React, { useEffect, useMemo, useRef, useState } from "react";
  import { useNavigate } from "react-router-dom";
  import { supabase } from "../../utils/supabase";
  import { loadBillingAttachments } from "../../utils/billing-attachments";

  const LOGO_URL =
    "https://qmrmkemkctikfoyxpanz.supabase.co/storage/v1/object/public/personalizacion/Logo%20Calins.png";

  const fmt = (n) =>
    new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      Number(n || 0)
    );
  const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
  const firstNonBlank = (...values) => {
    for (const value of values) {
      const text = String(value ?? "").trim();
      if (text) return text;
    }
    return "";
  };

  function getOsIdFromUrl() {
    const qp = new URLSearchParams(window.location.search);
    return qp.get("id");
  }

  function getInvoiceIdFromUrl() {
    const qp = new URLSearchParams(window.location.search);
    return qp.get("invoice_id");
  }

  // acepta “OS-5594-001” y “OS-5594001”
  function deriveCentroCtoFromCode(code) {
    const s = String(code || "");
    const m = s.match(/OS-(\d{4})-?\d+/);
    return m ? m[1] : "";
  }

  function normalizeServNro(code) {
    const m = String(code || "").match(/OS-(\d+-\d+)/);
    return m ? m[1] : String(code || "");
  }

  // TAG — CATEGORÍA: X — MARCA: Y, MOD: Z, S/N: W — PO: <baker_code>
  function buildDescription(i) {
    // Construir la parte base con tag y categoría
    let parts = [];

    if (i.instrument_tag) {
      parts.push(`TAG: ${i.instrument_tag}`);
    }

    if (i.instrument_category) {
      parts.push(`CATEGORÍA: ${i.instrument_category}`);
    }

    const base = parts.join(" — ");

    // Agregar marca, modelo y serial
    const serial = String(i.instrument_serial ?? "").trim() || "sin informar";
    const details = `MARCA: ${i.instrument_brand ?? ""}, MOD: ${
      i.instrument_model ?? ""
    }, S/N: ${serial}`;

    const fullBase = base ? `${base} — ${details}` : details;

    if (i.baker_code) return `${fullBase} — PO: ${i.baker_code}`;
    return fullBase;
  }

  // Normaliza diferentes formas de “filas” al formato de tabla del original
  function mapToMergedRows(items = []) {
    return (items || []).map((i) => {
      // ✅ Extras: permitir que el detalle de facturación muestre ítems varios
      // Si el item viene con extra_id (o marcado como extra), lo mostramos como una línea "EXTRA".
      const isExtra = !!(i.extra_id || i.is_extra || i.kind === "extra" || i.type === "extra");
      if (isExtra) {
        const qty = num(i.qty ?? i.quantity ?? i.cant ?? 1, 1);
        const pu = num(i.price ?? i.unit_price ?? i.pu ?? 0, 0);
        const centro =
          i.centro_cto ??
          i.cost_center ??
          i.centro_cto_code ??
          deriveCentroCtoFromCode(i.os_code || i.code || i.service_order_code || "");

        const desc =
          i.descripcion ||
          i.description ||
          i.extra_description ||
          (i.service_price_extras?.description ?? "Extra");

        return {
          centro_cto: centro || "",
          serv_nro: "EXTRA",
          descripcion: desc ? `EXTRA — ${desc}` : "EXTRA",
          cant: qty,
          pu,
          subtotal: qty * pu,
          note: firstNonBlank(i.note, i.item_note, i.service_price_extras?.note),
        };
      }

      const code = i.code || i.os_code || i.service_order_code || "";
      const qty = num(i.qty ?? i.quantity ?? i.cant ?? 1, 1);
      const pu = num(i.price ?? i.unit_price ?? i.pu ?? 0, 0);

      const instrument_tag = i.instrument_tag ?? i.instr_tag ?? i.tag ?? "";
      const instrument_brand = i.instrument_brand ?? i.instr_brand ?? i.brand ?? "";
      const instrument_model = i.instrument_model ?? i.instr_model ?? i.model ?? "";
      const instrument_serial = i.instrument_serial ?? i.instr_serial ?? i.serial ?? "";
      const baker_code = i.baker_code ?? i.instr_baker_code ?? null;
      const instrument_category = i.instrument_category ?? i.instr_category ?? i.category_name ?? null;

      return {
        centro_cto: i.centro_cto ?? deriveCentroCtoFromCode(code),
        serv_nro: i.serv_nro ?? normalizeServNro(code),
        descripcion:
          i.descripcion ??
          buildDescription({
            instrument_tag,
            instrument_brand,
            instrument_model,
            instrument_serial,
            baker_code,
            instrument_category,
          }),
        cant: qty,
        pu,
        subtotal: qty * pu,
        note: firstNonBlank(i.note, i.item_note, i.service_prices?.note),
      };
    });
  }

  // ✅ FIX facturación: resuelve el código/datos REALES de cada instrumento por su
  // instrument_id. Los service_prices guardan todos los ítems bajo UN solo os_id
  // (el de la oferta), pero cada instrumento pertenece a su propia OS. Sin esto,
  // el detalle solo mostraba la info del único instrumento que coincidía con ese os_id.
  async function enrichRowsByInstrument(rows = []) {
    const ids = Array.from(
      new Set((rows || []).map((r) => r.instrument_id ?? r.instr_id).filter(Boolean))
    );
    if (!ids.length) return rows;
    const { data } = await supabase
      .from("v_service_orders_enriched")
      .select(
        "instrument_id, code, instrument_tag, instrument_brand, instrument_model, instrument_serial"
      )
      .in("instrument_id", ids);
    const byInstr = new Map((data || []).map((r) => [r.instrument_id, r]));
    return (rows || []).map((r) => {
      const id = r.instrument_id ?? r.instr_id;
      const e = id ? byInstr.get(id) : null;
      if (!e) return r; // extras u otros sin instrumento: sin cambios
      return {
        ...r,
        code: e.code ?? r.code,
        instrument_tag: e.instrument_tag ?? r.instrument_tag ?? r.instr_tag,
        instrument_brand: e.instrument_brand ?? r.instrument_brand ?? r.instr_brand,
        instrument_model: e.instrument_model ?? r.instrument_model ?? r.instr_model,
        instrument_serial: e.instrument_serial ?? r.instrument_serial ?? r.instr_serial,
        // recomputar centro/serv desde el code correcto
        centro_cto: null,
        serv_nro: null,
        descripcion:
          r.descripcion && String(r.descripcion).trim() ? r.descripcion : null,
      };
    });
  }

  export default function FacturacionPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [header, setHeader] = useState(null);
    const [os, setOs] = useState(null);
    const [ocNumber, setOcNumber] = useState(null);
    const [ocPdfUrl, setOcPdfUrl] = useState(null);
    const [ocDocuments, setOcDocuments] = useState([]);
    const [remitoDocuments, setRemitoDocuments] = useState([]);
    const [rows, setRows] = useState([]);

    // Emails de facturación del cliente
    const [clientBillingEmails, setClientBillingEmails] = useState({ e1: null, e2: null });

    const contentRef = useRef(null);
    const osId = getOsIdFromUrl();
    const invoiceId = getInvoiceIdFromUrl();

    const navigate = useNavigate();

    // Función de navegación con fallback robusto
    const goToInbox = async () => {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;

      let role = null;
      if (session?.user?.id) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", session.user.id)
          .maybeSingle();
        role = prof?.role || null;
      }

      const baseTarget =
        role === "accountant"
          ? "/contabilidad/facturacion"
          : "/facturacion/laboratorio";
      const target = `${baseTarget}${invoiceId ? `?invoice_id=${invoiceId}` : ""}`;

      if (session) {
        try {
          navigate(target);
        } catch {
          window.location.href = target;
        }
        return;
      }

      const next = encodeURIComponent(target);
      window.location.href = `${window.location.origin}/login?next=${next}`;
    };

  async function loadSelectedInvoiceItems(pInvoiceId) {
      if (!pInvoiceId)
        return {
          instrumentIds: [],
          itemByInstrument: new Map(),
          extrasSelected: [],
          invoiceHeader: null,
        };

      // Intentar leer cabecera (opcional)
      let invoiceHeader = null;
      try {
        const { data: inv } = await supabase
          .from("billing_invoices")
          .select("id, invoice_number, issued_at, requested_at, status, note")
          .eq("id", pInvoiceId)
          .maybeSingle();
        invoiceHeader = inv || null;
      } catch {
        // ignore
      }

      // Leer items + join a service_prices para obtener instrument_id
      // ✅ y join a service_price_extras para obtener descripción de extras
    const { data: items, error: eItems } = await supabase
      .from("billing_invoice_items")
      .select(
        "id, invoice_id, service_price_id, extra_id, qty, price, currency, note, service_prices(os_id, instrument_id, qty, price, currency, note), service_price_extras(description, qty, price, currency, note, os_id)"
      )
      .eq("invoice_id", pInvoiceId);

      if (eItems) throw eItems;

      const instrumentIds = [];
      const itemByInstrument = new Map();
      const extrasSelected = [];

      for (const it of items || []) {
        // ✅ EXTRA
        if (it?.extra_id) {
          const ex = it?.service_price_extras;
          const qty = it.qty ?? ex?.qty ?? 1;
          const price = it.price ?? ex?.price ?? 0;
          const currency = it.currency ?? ex?.currency ?? "USD BNA";
          const note = it.note ?? ex?.note ?? "";
          const description = ex?.description ?? "Extra";

          extrasSelected.push({
            extra_id: it.extra_id,
            description,
            qty,
            price,
            currency,
            note,
          });
          continue;
        }

        const sp = it?.service_prices;
        const instrumentId = sp?.instrument_id;
        if (!instrumentId) continue;
        instrumentIds.push(instrumentId);

        const qty = it.qty ?? sp.qty ?? 1;
        const price = it.price ?? sp.price ?? 0;
        const currency = it.currency ?? sp.currency ?? "USD BNA";
        const note = it.note ?? sp.note ?? "";

        itemByInstrument.set(instrumentId, {
          instrument_id: instrumentId,
          qty,
          price,
          currency,
          note,
        });
      }

    return {
      instrumentIds: Array.from(new Set(instrumentIds)),
      itemByInstrument,
      extrasSelected,
      invoiceHeader,
      resolvedOsId:
        (items || [])
          .map((it) => it?.service_prices?.os_id || it?.service_price_extras?.os_id || null)
          .find(Boolean) || null,
    };
  }

  async function getBillingHeaderByOs(pOsId) {
    if (!pOsId) return null;
    try {
      const { data, error } = await supabase.rpc("get_billing_request", { p_os_id: pOsId });
      if (error) return null;
      const hdr = Array.isArray(data) ? data[0] : data;
      return hdr || null;
    } catch {
      return null;
    }
  }

  // Obtiene remito para la OS actual; si no tiene, busca uno en cualquier OS del mismo PO.
  async function resolveRemitoPdfUrl(pOsId, pPoId) {
    const tried = new Set();

    const tryOs = async (candidateOsId) => {
      if (!candidateOsId || tried.has(candidateOsId)) return null;
      tried.add(candidateOsId);
      const hdr = await getBillingHeaderByOs(candidateOsId);
      return hdr?.remito_pdf_url || null;
    };

    const direct = await tryOs(pOsId);
    if (direct) return direct;
    if (!pPoId) return null;

    try {
      const { data: soRows, error } = await supabase
        .from("service_orders")
        .select("id, created_at")
        .eq("po_id", pPoId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) return null;

      for (const soRow of soRows || []) {
        const url = await tryOs(soRow.id);
        if (url) return url;
      }
    } catch {
      return null;
    }

    return null;
  }

    useEffect(() => {
      let active = true;

      async function load() {
        try {
          setLoading(true);
          setError(null);
          setClientBillingEmails({ e1: null, e2: null });
          setHeader(null);
          setOs(null);
          setOcNumber(null);
          setOcPdfUrl(null);
          setOcDocuments([]);
          setRemitoDocuments([]);
          setRows([]);

          if (!osId && !invoiceId) {
            throw new Error("Falta ?id con el ID de la Orden de Servicio o ?invoice_id con la factura.");
          }

          // Obtener Token de la URL
          const token = new URLSearchParams(window.location.search).get("t");

          // ¿hay sesión?
          const {
            data: { session },
          } = await supabase.auth.getSession();

          // =========================================================
          // MODO PÚBLICO (SIN SESSION)
          // =========================================================
          if (!session) {
            // A) CASO CON INVOICE ID: Usar RPC protegido por token
            if (invoiceId) {
              if (!token) throw new Error("Este link requiere token (t).");

              const { data, error: rpcErr } = await supabase.rpc("get_facturacion_invoice_public", {
                p_invoice_id: invoiceId,
                p_token: token,
              });

              if (rpcErr) throw rpcErr;
              if (!data) throw new Error("No se encontraron datos para esta factura.");

              // Compatibilidad de payload:
              // - Formato viejo: { header, items, so }
              // - Formato actual del RPC: { invoice, items }
              const rpcItems = data.items || [];
              const rpcHeaderBase = data.header || data.invoice || null;
            const rpcSo = data.so || null;
            const resolvedOsId = rpcSo?.id || rpcItems?.[0]?.os_id || osId || null;
            let resolvedPoId = rpcSo?.po_id || rpcHeaderBase?.po_id || null;

              let finalOcNumber =
                rpcHeaderBase?.oc_number || rpcHeaderBase?.accept_reference || null;
              let finalOcPdf = rpcHeaderBase?.oc_pdf_url || null;

              // Fallback de OC por OS si el RPC no la trae
              if (resolvedOsId && (!finalOcPdf || !finalOcNumber)) {
                try {
                  const { data: offerData } = await supabase.rpc("get_offer_for_os", {
                    p_os_id: resolvedOsId,
                  });
                  const offerRow = Array.isArray(offerData) ? offerData[0] : offerData;
                  finalOcNumber =
                    finalOcNumber || offerRow?.oc_number || offerRow?.accept_reference || null;
                  finalOcPdf = finalOcPdf || offerRow?.oc_pdf_url || null;
                } catch (e) {
                  console.warn("[OC fallback invoice public]", e);
                }
              }

            if (!resolvedPoId && resolvedOsId) {
              try {
                const { data: soRow } = await supabase
                  .from("service_orders")
                  .select("po_id")
                  .eq("id", resolvedOsId)
                  .maybeSingle();
                resolvedPoId = soRow?.po_id || null;
              } catch {
                // ignore
              }
            }

            let remitoPdfUrl = rpcHeaderBase?.remito_pdf_url || null;
            if (!remitoPdfUrl && resolvedOsId) {
              remitoPdfUrl = await resolveRemitoPdfUrl(resolvedOsId, resolvedPoId);
            }
            const attachmentCollections = await loadBillingAttachments({
              osId: resolvedOsId,
              poId: resolvedPoId,
              offerId: rpcHeaderBase?.offer_id || null,
              fallbackOcDocuments: rpcHeaderBase?.oc_documents || [],
              fallbackOcUrl: finalOcPdf,
              fallbackOcNumber: finalOcNumber,
              fallbackRemitoDocuments: rpcHeaderBase?.remito_documents || [],
              fallbackRemitoUrl: remitoPdfUrl,
            });

              const rpcHeader = rpcHeaderBase
                ? {
                    ...rpcHeaderBase,
                    oc_number: finalOcNumber ?? rpcHeaderBase?.oc_number ?? null,
                    oc_pdf_url: finalOcPdf ?? rpcHeaderBase?.oc_pdf_url ?? null,
                    remito_pdf_url: remitoPdfUrl ?? rpcHeaderBase?.remito_pdf_url ?? null,
                  }
                : {
                    oc_number: finalOcNumber,
                    oc_pdf_url: finalOcPdf,
                    remito_pdf_url: remitoPdfUrl,
                  };

              // Mapeamos los items devueltos por el RPC
              const merged = mapToMergedRows(rpcItems);

              if (!active) return;
              setHeader(rpcHeader);
              setOs(rpcSo || (resolvedOsId ? { id: resolvedOsId } : null));
              setOcNumber(rpcHeader?.oc_number || null);
              setOcPdfUrl(rpcHeader?.oc_pdf_url || null);
              setOcDocuments(attachmentCollections.ocDocuments);
              setRemitoDocuments(attachmentCollections.remitoDocuments);

              // Emails
              const e1 =
                rpcHeader?.billing_email_1 || rpcHeader?.client_billing_email_1 || null;
              const e2 =
                rpcHeader?.billing_email_2 || rpcHeader?.client_billing_email_2 || null;
              setClientBillingEmails({ e1, e2 });

              setRows(merged);
              return;
            }

            // B) CASO SIN INVOICE ID (Ver OS completa): Usar RPC público antiguo
            try {
              const { data, error: rpcErr } = await supabase.rpc("get_facturacion_public", {
                p_os_id: osId,
              });
              if (rpcErr) throw rpcErr;

              const hdr = data?.header ?? null;
              const so = data?.so ?? null;
              const offer = data?.offer ?? null;

              const ocNum =
                offer?.oc_number ||
                offer?.accept_reference ||
                hdr?.oc_number ||
                hdr?.accept_reference ||
                null;
              const ocPdf = offer?.oc_pdf_url || hdr?.oc_pdf_url || null;

              const clientEmails = data?.clientBillingEmails ?? {};
              const e1 =
                clientEmails?.e1 ??
                hdr?.billing_email_1 ??
                hdr?.client_billing_email_1 ??
                null;
              const e2 =
                clientEmails?.e2 ??
                hdr?.billing_email_2 ??
                hdr?.client_billing_email_2 ??
                null;

              // Enriquecer filas con categorías
              let enrichedRows = data?.rows ?? [];
              const instrumentIds = enrichedRows
                .map((r) => r.instrument_id ?? r.instr_id)
                .filter(Boolean);

              if (instrumentIds.length > 0) {
                try {
                  const { data: categoriesData } = await supabase
                    .from("instruments")
                    .select("id, instrument_categories(name)")
                    .in("id", instrumentIds);

                  const categoryMap = new Map(
                    (categoriesData || []).map((c) => [c.id, c.instrument_categories?.name || null])
                  );

                  enrichedRows = enrichedRows.map((row) => ({
                    ...row,
                    instrument_category:
                      row.instrument_category ||
                      categoryMap.get(row.instrument_id ?? row.instr_id) ||
                      null,
                  }));
                } catch (catErr) {
                  console.warn("[categorías en modo público]", catErr);
                }
              }

              enrichedRows = await enrichRowsByInstrument(enrichedRows);
              const merged = mapToMergedRows(enrichedRows);

              // Fallbacks PDF
              let finalOcPdf = ocPdf;
              if (!finalOcPdf && so?.id) {
                try {
                  const { data: offerData } = await supabase.rpc("get_offer_for_os", {
                    p_os_id: so.id,
                  });
                  const offerRow = Array.isArray(offerData) ? offerData[0] : offerData;
                  finalOcPdf = offerRow?.oc_pdf_url || null;
                } catch (e) {
                  console.warn("[OC PDF fallback]", e);
                }
              }

            let remitoPdfUrl = hdr?.remito_pdf_url || null;
            if (!remitoPdfUrl && so?.id) {
              remitoPdfUrl = await resolveRemitoPdfUrl(so.id, so?.po_id || null);
            }
            const enrichedHeader = hdr ? { ...hdr, remito_pdf_url: remitoPdfUrl } : hdr;
            const attachmentCollections = await loadBillingAttachments({
              osId: so?.id || osId,
              poId: so?.po_id || null,
              offerId: offer?.id || hdr?.offer_id || null,
              fallbackOcDocuments: offer?.oc_documents || hdr?.oc_documents || [],
              fallbackOcUrl: finalOcPdf,
              fallbackOcNumber: ocNum,
              fallbackRemitoDocuments: hdr?.remito_documents || [],
              fallbackRemitoUrl: remitoPdfUrl,
            });

              if (!active) return;
              setHeader(enrichedHeader);
              setOs(so);
              setOcNumber(ocNum);
              setOcPdfUrl(finalOcPdf);
              setOcDocuments(attachmentCollections.ocDocuments);
              setRemitoDocuments(attachmentCollections.remitoDocuments);
              setClientBillingEmails({ e1, e2 });
              setRows(merged);
              return;
            } catch (e) {
              // Fallback: get_billing_request
              try {
                const { data: hdrArr, error: hdrErr } = await supabase.rpc("get_billing_request", {
                  p_os_id: osId,
                });
                if (hdrErr) throw hdrErr;

                const arr = Array.isArray(hdrArr) ? hdrArr : hdrArr ? [hdrArr] : [];
                const hdr = arr[0] || null;
                if (!hdr) throw new Error("No se pudo cargar la cabecera.");

                const maybeRows = hdr?.rows || hdr?.items || null;
                let rowsToMap = Array.isArray(maybeRows) ? maybeRows : arr;

                // Enriquecer categorías fallback
                const instrumentIds = rowsToMap
                  .map((r) => r.instrument_id ?? r.instr_id)
                  .filter(Boolean);

                if (instrumentIds.length > 0) {
                  try {
                    const { data: categoriesData } = await supabase
                      .from("instruments")
                      .select("id, instrument_categories(name)")
                      .in("id", instrumentIds);
                    const categoryMap = new Map(
                      (categoriesData || []).map((c) => [c.id, c.instrument_categories?.name || null])
                    );
                    rowsToMap = rowsToMap.map((row) => ({
                      ...row,
                      instrument_category:
                        row.instrument_category ||
                        categoryMap.get(row.instrument_id ?? row.instr_id) ||
                        null,
                    }));
                  } catch (catErr) {
                    // ignore
                  }
                }

                rowsToMap = await enrichRowsByInstrument(rowsToMap);
                const merged = mapToMergedRows(rowsToMap);

                // PDF Fallbacks
                const ocNum = hdr.oc_number || hdr.accept_reference || null;
                let ocPdf = hdr.oc_pdf_url || null;
                if (!ocPdf && osId) {
                  try {
                    const { data: offerData } = await supabase.rpc("get_offer_for_os", {
                      p_os_id: osId,
                    });
                    const offerRow = Array.isArray(offerData) ? offerData[0] : offerData;
                    ocPdf = offerRow?.oc_pdf_url || null;
                  } catch (e) { /* ignore */ }
                }
              let remitoPdfUrl = hdr.remito_pdf_url || null;
              if (!remitoPdfUrl && osId) {
                remitoPdfUrl = await resolveRemitoPdfUrl(osId, hdr?.po_id || null);
              }
              const attachmentCollections = await loadBillingAttachments({
                osId,
                poId: hdr?.po_id || null,
                offerId: hdr?.offer_id || null,
                fallbackOcDocuments: hdr?.oc_documents || [],
                fallbackOcUrl: ocPdf,
                fallbackOcNumber: ocNum,
                fallbackRemitoDocuments: hdr?.remito_documents || [],
                fallbackRemitoUrl: remitoPdfUrl,
              });

                const enrichedHeader = { ...hdr, remito_pdf_url: remitoPdfUrl };
                const e1 = hdr.billing_email_1 || hdr.client_billing_email_1 || null;
                const e2 = hdr.billing_email_2 || hdr.client_billing_email_2 || null;

                const so = {
                  id: osId,
                  code: hdr.os_code || hdr.code || "",
                  po_id: hdr.po_id || null,
                  general_observations: hdr.general_observations || null,
                };

                if (!active) return;
                setHeader(enrichedHeader);
                setOs(so);
                setOcNumber(ocNum);
                setOcPdfUrl(ocPdf);
                setOcDocuments(attachmentCollections.ocDocuments);
                setRemitoDocuments(attachmentCollections.remitoDocuments);
                setClientBillingEmails({ e1, e2 });
                setRows(merged);
                return;
              } catch (e2) {
                throw new Error(
                  "No se pudo cargar en modo público. Falta habilitar el RPC público o el token es inválido."
                );
              }
            }
          }

          // =========================================================
          // MODO LOGUEADO (SESSION ACTIVA)
          // =========================================================

          // 1. Si viene invoice_id, cargamos selección y VALIDAMOS
          let selected = {
            instrumentIds: [],
            itemByInstrument: new Map(),
            extrasSelected: [],
            invoiceHeader: null,
            resolvedOsId: null,
          };

          if (invoiceId) {
            selected = await loadSelectedInvoiceItems(invoiceId);

            // Puede haber factura solo con extras (sin instrumentos). Validamos con ambos.
            if (!selected.instrumentIds?.length && !(selected.extrasSelected || []).length) {
              throw new Error(
                "El link trae invoice_id pero no se pudieron cargar ítems de esa factura. " +
                  "Probable RLS/permisos o la factura está vacía."
              );
            }
          }

          const effectiveOsId = osId || selected.resolvedOsId;
          if (!effectiveOsId) {
            throw new Error(
              "No pudimos determinar la Orden de Servicio desde la URL ni desde la factura (invoice_id)."
            );
          }

          // 2. Cargar OS básica
          const { data: so, error: eSo } = await supabase
            .from("service_orders")
            .select("id, code, po_id, general_observations")
            .eq("id", effectiveOsId)
            .maybeSingle();
          if (eSo) throw eSo;
          if (!so) throw new Error("Orden de servicio no encontrada.");

          // 3. Header
          let hdr = null;
          try {
            const { data: hdrArr } = await supabase.rpc("get_billing_request", { p_os_id: effectiveOsId });
            hdr = Array.isArray(hdrArr) ? hdrArr[0] : null;
          } catch {
            // fallback
          }

          // 4. OC / PDFs
          let ocNum = null;
          let ocPdf = null;
          try {
            const { data: rpcOffer } = await supabase.rpc("get_offer_for_os", { p_os_id: effectiveOsId });
            const row = Array.isArray(rpcOffer) ? rpcOffer[0] : rpcOffer;
            if (row) {
              ocNum = row.oc_number || row.accept_reference || null;
              ocPdf = row.oc_pdf_url || null;
            }
          } catch {
            // ignore
          }
        if (!ocNum && hdr) ocNum = hdr.oc_number || hdr.accept_reference || null;
        if (!ocPdf && hdr) ocPdf = hdr.oc_pdf_url || null;

        // Remito PDF (modo logueado): usar cabecera y fallback por PO
        let remitoPdfUrl = hdr?.remito_pdf_url || null;
        if (!remitoPdfUrl && so?.id) {
          remitoPdfUrl = await resolveRemitoPdfUrl(so.id, so?.po_id || null);
        }
        const attachmentCollections = await loadBillingAttachments({
          osId: so?.id,
          poId: so?.po_id || null,
          offerId: hdr?.offer_id || null,
          fallbackOcDocuments: hdr?.oc_documents || [],
          fallbackOcUrl: ocPdf,
          fallbackOcNumber: ocNum,
          fallbackRemitoDocuments: hdr?.remito_documents || [],
          fallbackRemitoUrl: remitoPdfUrl,
        });

          // 5. Emails client
          let clientId = hdr?.client_id ?? null;
          if (!clientId && so?.po_id) {
            try {
              const { data: poRow } = await supabase
                .from("purchase_orders")
                .select("client_id, offer_id")
                .eq("id", so.po_id)
                .maybeSingle();
              clientId = poRow?.client_id ?? null;
            } catch {
              // ignore
            }
          }
          if (clientId) {
            try {
              const { data: cRow, error: cErr } = await supabase
                .from("clients")
                .select("billing_email_1, billing_email_2")
                .eq("id", clientId)
                .maybeSingle();
              if (!cErr && cRow) {
                setClientBillingEmails({
                  e1: cRow.billing_email_1 || null,
                  e2: cRow.billing_email_2 || null,
                });
              }
            } catch {
              // ignore
            }
          }

          // 6. Instrumentos (filtrado por selección si aplica)
          let inst = [];
          if (invoiceId && selected.instrumentIds?.length) {
            const { data } = await supabase
              .from("v_service_orders_enriched")
              .select(
                "po_id,instrument_id,code,instrument_tag,instrument_brand,instrument_model,instrument_serial"
              )
              .eq("po_id", so.po_id)
              .in("instrument_id", selected.instrumentIds)
              .order("code", { ascending: true });
            inst = data || [];
          } else {
            const { data } = await supabase
              .from("v_service_orders_enriched")
              .select(
                "po_id,instrument_id,code,instrument_tag,instrument_brand,instrument_model,instrument_serial"
              )
              .eq("po_id", so.po_id)
              .order("code", { ascending: true });
            inst = data || [];
          }

          // 7. Info extra (baker / category)
          let bakerMap = new Map();
          let categoryMap = new Map();
          const ids = (inst || []).map((r) => r.instrument_id).filter(Boolean);
          const uniqueIds = Array.from(new Set(ids));

          if (uniqueIds.length > 0) {
            const { data: instExtra, error: instExtraErr } = await supabase
              .from("instruments")
              .select("id, baker_code, instrument_categories(name)")
              .in("id", uniqueIds);

            if (instExtraErr) throw instExtraErr;

            bakerMap = new Map((instExtra || []).map((i) => [i.id, i.baker_code]));
            categoryMap = new Map(
              (instExtra || []).map((i) => [i.id, i.instrument_categories?.name || null])
            );
          }

          // 8. Precios
          let priceMap = new Map();
          if (invoiceId && selected.instrumentIds?.length) {
            const { data: prices } = await supabase
              .from("service_prices")
              .select("os_id,instrument_id,qty,price,note,currency")
              .eq("os_id", so.id)
              .in("instrument_id", selected.instrumentIds);
            for (const p of prices || []) priceMap.set(p.instrument_id, p);
          } else {
            const { data: prices } = await supabase
              .from("service_prices")
              .select("os_id,instrument_id,qty,price,note,currency")
              .eq("os_id", so.id);
            for (const p of prices || []) priceMap.set(p.instrument_id, p);
          }

          // 9. Merge final (instrumentos)
          const mergedInstruments = (inst || []).map((i) => {
            const p = priceMap.get(i.instrument_id);
            const sel = selected.itemByInstrument.get(i.instrument_id);

            const qty = num(sel?.qty ?? p?.qty ?? 1, 1);
            const pu = num(sel?.price ?? p?.price ?? 0, 0);
            const baker_code = bakerMap.get(i.instrument_id) || null;
            const instrument_category = categoryMap.get(i.instrument_id) || null;

            return {
              centro_cto: deriveCentroCtoFromCode(i.code),
              serv_nro: normalizeServNro(i.code),
              descripcion: buildDescription({ ...i, baker_code, instrument_category }),
              cant: qty,
              pu,
              subtotal: qty * pu,
              note: firstNonBlank(sel?.note, p?.note),
            };
          });

          // 10. Extras seleccionados en la factura (si aplica)
          const mergedExtras = (selected.extrasSelected || []).map((ex) => {
            const centro =
              deriveCentroCtoFromCode(so?.code || hdr?.os_code || "") ||
              mergedInstruments?.[0]?.centro_cto ||
              hdr?.cost_center ||
              "";

            const qty = num(ex.qty ?? 1, 1);
            const pu = num(ex.price ?? 0, 0);
            return {
              centro_cto: centro,
              serv_nro: "EXTRA",
              descripcion: ex.description ? `EXTRA — ${ex.description}` : "EXTRA",
              cant: qty,
              pu,
              subtotal: qty * pu,
              note: ex.note || "",
            };
          });

          const merged = [...mergedInstruments, ...mergedExtras];

          if (!active) return;
          setOs(so);
        const baseHeader = hdr ? { ...hdr, remito_pdf_url: remitoPdfUrl } : { remito_pdf_url: remitoPdfUrl };
        const headerWithInvoice = selected.invoiceHeader
          ? { ...baseHeader, invoice: selected.invoiceHeader }
          : baseHeader;
          setHeader(headerWithInvoice);
          setOcNumber(ocNum || null);
          setOcPdfUrl(ocPdf || null);
          setOcDocuments(attachmentCollections.ocDocuments);
          setRemitoDocuments(attachmentCollections.remitoDocuments);
          setRows(merged);
        } catch (err) {
          setError(err?.message ?? String(err));
        } finally {
          if (active) setLoading(false);
        }
      }

      load();
      return () => {
        active = false;
      };
    }, [osId, invoiceId]);

    const orderedRows = useMemo(
      () => [...rows].sort((a, b) => String(a.serv_nro).localeCompare(String(b.serv_nro))),
      [rows]
    );
    const total = useMemo(
      () => orderedRows.reduce((acc, r) => acc + num(r.subtotal, 0), 0),
      [orderedRows]
    );

    async function handleExportPDF() {
      try {
        const html2pdf = (await import("html2pdf.js")).default;
        const node = contentRef.current.cloneNode(true);
        const actions = node.querySelector("#actions-bar");
        if (actions) actions.remove();
        await html2pdf()
          .set({
            margin: [10, 10, 14, 10],
            filename: `Detalle_Facturacion_${os?.code || osId}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["avoid-all", "css", "legacy"] },
          })
          .from(node)
          .save();
      } catch {
        alert("No se pudo generar el PDF.");
      }
    }

    if (loading)
      return (
        <div className="min-h-screen grid place-content-center text-gray-700">
          Cargando detalle de facturación…
        </div>
      );
    if (error)
      return (
        <div className="min-h-screen grid place-content-center text-red-700">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="font-semibold mb-1">No pudimos cargar la información</div>
            <div className="text-sm">{error}</div>
          </div>
        </div>
      );

    const ocLink = ocPdfUrl || header?.oc_pdf_url || null;
    const displayedOcDocuments = ocDocuments.length
      ? ocDocuments
      : ocLink
        ? [{ id: "oc-fallback", url: ocLink, label: ocNumber ? `OC ${ocNumber}` : "Orden de compra" }]
        : [];
    const displayedRemitoDocuments = remitoDocuments.length
      ? remitoDocuments
      : header?.remito_pdf_url
        ? [{ id: "remito-fallback", url: header.remito_pdf_url, label: "Remito" }]
        : [];
    const hasBillingEmails = !!(clientBillingEmails?.e1 || clientBillingEmails?.e2);

    return (
      <div className="min-h-screen bg-gray-100 py-6">
        <div className="max-w-5xl mx-auto">
          <div id="actions-bar" className="flex items-center justify-end gap-2 mb-3">
            <button
              onClick={goToInbox}
              className="px-3 py-2 rounded-md bg-slate-700 text-white hover:bg-slate-800 text-sm"
              title="Volver a la bandeja de pedidos de facturación"
            >
              Bandeja de facturación
            </button>

            <button
              onClick={handleExportPDF}
              className="px-3 py-2 rounded-md bg-sky-600 text-white hover:bg-sky-700 text-sm"
            >
              Descargar PDF
            </button>
          </div>

          <div
            ref={contentRef}
            className="bg-white shadow-lg rounded-xl overflow-hidden print:shadow-none print:rounded-none"
          >
            <header className="border-b border-gray-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-sky-50 to-white">
              <div className="flex items-center gap-4">
                <img src={LOGO_URL} alt="Logo" className="h-12 w-auto object-contain" />
                <div>
                  <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                    Detalle de Facturación
                  </h1>
                  <p className="text-sm text-gray-600">
                    Generado automáticamente por el sistema de gestión
                  </p>
                </div>
              </div>
              <div className="mt-4 md:mt-0 text-right text-sm text-gray-800">
                <p>
                  <b>OS:</b> {os?.code || header?.os_code || "—"}
                </p>
                <p>
                  <b>Fecha:</b>{" "}
                  {new Date().toLocaleDateString("es-AR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })}
                </p>
              </div>
            </header>

            <section className="px-6 pt-4 pb-2 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-800">
              <div className="space-y-1">
                <div>
                  <span className="text-gray-700">Cliente:</span> {header?.client_name ?? "—"}
                </div>
                <div>
                  <span className="text-gray-700">CUIT:</span> {header?.client_cuit ?? "—"}
                </div>
                <div>
                  <span className="text-gray-700">Cliente Nº:</span> {header?.client_number ?? "—"}
                </div>

                {/* emails de facturación */}
                {hasBillingEmails ? (
                  <div className="pt-1">
                    <span className="text-gray-700">Emails facturación:</span>{" "}
                    <span className="text-gray-900">
                      {clientBillingEmails?.e1 ? clientBillingEmails.e1 : ""}
                      {clientBillingEmails?.e2 ? `, ${clientBillingEmails.e2}` : ""}
                    </span>
                  </div>
                ) : null}

                {ocNumber && (
                  <div>
                    <span className="text-gray-700">OC Nº:</span> {ocNumber}
                  </div>
                )}
              </div>

              <div className="space-y-1 md:text-right">
                <div>
                  <span className="text-gray-700">Forma de pago:</span>{" "}
                  {header?.payment_method || header?.payment_terms || "—"}
                </div>

                <div>
                  <span className="text-gray-700">Moneda:</span>{" "}
                  {header?.price_currency || header?.currency || "USD BNA"}
                </div>

                <div>
                  <span className="text-gray-700">Centro de Costo:</span>{" "}
                  {header?.cost_center ?? deriveCentroCtoFromCode(os?.code || header?.os_code) ?? "—"}
                </div>
              </div>
            </section>

            <section className="px-6 pb-3 text-sm text-gray-800">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 font-semibold text-gray-900">Documentos adjuntos</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                      Órdenes de compra ({displayedOcDocuments.length})
                    </div>
                    {displayedOcDocuments.length ? (
                      <div className="space-y-1">
                        {displayedOcDocuments.map((document, index) => (
                          <a
                            key={document.id || document.url || index}
                            className="flex items-center gap-2 rounded border border-sky-100 bg-white px-2 py-1.5 text-sky-700 hover:bg-sky-50"
                            href={document.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span aria-hidden="true">📎</span>
                            <span className="underline">
                              {document.label || `Orden de compra ${index + 1}`}
                            </span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">No hay órdenes de compra adjuntas</span>
                    )}
                  </div>

                  <div>
                    <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                      Remitos ({displayedRemitoDocuments.length})
                    </div>
                    {displayedRemitoDocuments.length ? (
                      <div className="space-y-1">
                        {displayedRemitoDocuments.map((document, index) => (
                          <a
                            key={document.id || document.url || index}
                            className="flex items-center gap-2 rounded border border-sky-100 bg-white px-2 py-1.5 text-sky-700 hover:bg-sky-50"
                            href={document.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span aria-hidden="true">📎</span>
                            <span className="underline">
                              {document.label || `Remito ${index + 1}`}
                            </span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">No hay remitos adjuntos</span>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* === Tabla con Centro de Costo y PO en descripción === */}
            <section className="p-6 text-gray-900">
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full min-w-[900px] table-fixed text-sm break-inside-avoid">
                  <thead>
                    <tr className="bg-blue-100 text-gray-900">
                      <th className="w-[9%] px-3 py-2 text-left font-semibold">CENTRO CTO</th>
                      <th className="w-[11%] px-3 py-2 text-left font-semibold">SERV Nro.</th>
                      <th className="w-[30%] px-3 py-2 text-left font-semibold">DESCRIPCIÓN</th>
                      <th className="w-[22%] px-3 py-2 text-left font-semibold">NOTA</th>
                      <th className="w-[7%] px-3 py-2 text-right font-semibold">CANT.</th>
                      <th className="w-[10%] px-3 py-2 text-right font-semibold">PU</th>
                      <th className="w-[11%] px-3 py-2 text-right font-semibold">SUBTOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedRows.map((r, idx) => (
                      <tr
                        key={`${r.serv_nro}-${idx}`}
                        className={idx % 2 ? "bg-white text-gray-800" : "bg-gray-50 text-gray-800"}
                      >
                        <td className="px-3 py-2">{r.centro_cto}</td>
                        <td className="px-3 py-2">{r.serv_nro}</td>
                        <td className="break-words px-3 py-2 leading-tight">{r.descripcion}</td>
                        <td className="px-3 py-2 align-top">
                          {String(r.note ?? "").trim() ? (
                            <div className="whitespace-pre-wrap break-words rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs leading-snug text-gray-800">
                              {String(r.note).trim()}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{fmt(r.cant)}</td>
                        <td className="px-3 py-2 text-right">{fmt(r.pu)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmt(r.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-sky-100 border-t border-gray-300 font-semibold text-gray-900">
                      <td colSpan={6} className="p-2 text-right">
                        Total
                      </td>
                      <td className="p-2 text-right">{fmt(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {os?.general_observations && (
                <div className="mt-4 rounded-md border border-blue-200 bg-blue-50">
                  <div className="px-3 py-2 border-b border-blue-200 font-medium text-gray-800">
                    🔎 Observaciones Generales
                  </div>
                  <div className="p-3 text-gray-800 whitespace-pre-wrap">{os.general_observations}</div>
                </div>
              )}
            </section>

            <footer className="text-center text-xs text-gray-600 py-4 border-t border-gray-200 bg-gray-50">
              Calins Laboratorio · Sistema de Gestión — {new Date().getFullYear()}
            </footer>
          </div>
        </div>
      </div>
    );
  }
